// util
import {
  Inconveniences,
  SchedulingAttendanceSummaryTag,
  Unavailabilities,
} from "@clockwise/schema";
import { niceNameFromPerson, niceNamesAsString } from "./event";

export type Conflicts = "Conflict";
export type Pain = Inconveniences | Unavailabilities | Conflicts;

export interface IPersonProfile {
  givenName?: string | null | undefined;
  familyName?: string | null | undefined;
}

export interface IPerson {
  profile?: IPersonProfile | null | undefined;
  primaryEmail: string;
  isYou: boolean;
}

export interface ISchedulingAttendanceSummary {
  focusTimeMinutesCost: number;
  conflictingAttendees: string[];
  inconvenientForAttendees: string[];
  unavailableAttendees: string[];
  tags: SchedulingAttendanceSummaryTag[];
}

export interface ISchedulingAttendanceImpact {
  calendarId: string;
  focusTimeMinutesCost: number;
  inconveniences: Inconveniences[];
  unavailabilities: Unavailabilities[];
  hasConflict: boolean;
  conflictingMeetings: string[];
}

export interface ISchedulingSlot {
  summary: ISchedulingAttendanceSummary;
  attendanceImpacts: ISchedulingAttendanceImpact[];
}

// For private meetings the backend does not give back private meetings
// But we still want to render slot-pain messages with indidcating conflicts w/
// private meetings. This unfortunately also can happen from a bug in the BE where
// the newly conflicting meeting was also originally conflicting with another event.
// Because we can't correctly identify why no meeting title is returned, we'll just
// generically say the event is conflicting with "a meeting"
const GENERIC_MEETING_TITLE = "a meeting";
export const hasOnlyConflictingMeetingWithUnknownMeeting = (impact: {
  conflictingMeetings: string[] | null;
  hasConflict: boolean;
}) => {
  // When an attendee has a conflict with only private meetings, (or due to aformentioned BE bug),
  // the BE will return `hasConflict` as `true` and `conflictingMeetings` as empty, but
  return impact.hasConflict && impact?.conflictingMeetings?.length == 0;
};

/////////////
// Helpers //
/////////////

function generateInconvenienceMap(
  attendanceImpacts: { calendarId: string; inconveniences: Inconveniences[] }[],
) {
  // Dictionary of the possible inconveniences to array of calendarIds with that incon,
  // generated directly from Inconveniences.
  const inconvenienceMap: { [key: string]: string[] } = {};
  for (const inconvenience in Inconveniences) {
    inconvenienceMap[inconvenience] = [];
  }

  // Populate the map from attendanceImpacts
  attendanceImpacts.map((impact) => {
    impact.inconveniences.map((inconvenience) =>
      inconvenienceMap[inconvenience]?.push?.(impact.calendarId),
    );
  });

  return inconvenienceMap;
}

function generateUnavailabilityMap(
  attendanceImpacts: { calendarId: string; unavailabilities: string[] }[],
) {
  const unavailabilityMap: { [key: string]: string[] } = {
    FarOutsideWorkingHours: [],
  };

  attendanceImpacts.map((impact) => {
    impact.unavailabilities.map((unavailability) =>
      unavailabilityMap?.[unavailability].push(impact.calendarId),
    );
  });

  return unavailabilityMap;
}

type Person = {
  primaryEmail: string;
  profile?: {
    givenName?: string | null;
    familyName?: string | null;
  } | null;
  isYou: boolean;
};
export function getAttendeesText(
  persons: Person[],
  attendees: string[],
  isLeading = false,
): {
  attendeesText: string;
  isPlural: boolean;
  numImpacted: number;
} {
  // determine name occurrences for abbreviations on persons with conflicting names (Doug F., Doug P.)
  const attendeeGivenNameOccurrenceMap: { [key: string]: number } = {};

  persons
    .filter((person) => person.profile)
    .forEach((person) => {
      const givenName = person.profile ? person.profile.givenName : person.primaryEmail;

      if (!givenName) {
        return;
      }

      const attendeeGivenNameCount = attendeeGivenNameOccurrenceMap[givenName] || 0;
      attendeeGivenNameOccurrenceMap[givenName] = attendeeGivenNameCount + 1;
    });

  let foundYou = false;

  type PersonTuple = [string, Person | undefined];

  const attendeePersonTuples: PersonTuple[] = attendees.map((attendee: string) => [
    attendee,
    persons.find((person) => person.primaryEmail === attendee),
  ]);

  // compose a list of attendee names utilizing enhanced display names when possible
  const attendeeNames = attendeePersonTuples
    .filter(([_attendee, person]) => {
      if (person && person.profile) {
        // set outer foundYou for later usage and return false if the current person isYou
        foundYou = foundYou || person.isYou;
        return !person || !person.isYou;
      }

      return true;
    })
    .map(([attendee, person]) => {
      let displayName = attendee;

      if (person && person.profile) {
        displayName =
          niceNameFromPerson(
            person,
            attendees.length === 1,
            (person.profile.givenName &&
              attendeeGivenNameOccurrenceMap[person.profile.givenName] > 1 &&
              attendees.length !== 1) ||
              false,
          ) || "";
      }

      return displayName;
    })
    .sort();

  // add you to the front if you are an attendee
  if (foundYou) {
    attendeeNames.splice(0, 0, isLeading ? "You" : "you");
  }

  return {
    attendeesText: niceNamesAsString(attendeeNames, undefined, 3),
    isPlural: attendeeNames.length > 1 || foundYou,
    numImpacted: attendeeNames.length,
  };
}

function getInconvenienceStringForAttendees(pain: Pain, attendeesText: string) {
  if (pain === Inconveniences.External && attendeesText) {
    return (
      attendeesText +
      (attendeesText.includes(" ") ? " are " : " is ") +
      " outside your domain. This time may not work for them"
    );
  }

  if (pain === Inconveniences.Uncrawled && attendeesText) {
    return (
      "Clockwise can't access " + attendeesText + "'s calendar. This time may not work for them."
    );
  }

  let displayKey = "Inconvenient";
  switch (pain) {
    case Inconveniences.MissedLunch:
      displayKey = "Over lunch";
      break;
    case Inconveniences.OutsideMeetingHours:
      displayKey = "Outside meeting hours";
      break;
    case Inconveniences.OutsideWorkingHours:
      displayKey = "Outside working hours";
      break;
    case Inconveniences.OnNoMeetingDay:
      displayKey = "On a no-meeting day";
      break;
    case Inconveniences.NoMeetingRelief:
      displayKey = "Many consecutive meeting hours";
      break;
    case Inconveniences.Other:
    default:
      break;
  }
  return `${displayKey} for ${attendeesText}`;
}

//////////
// Pain //
//////////

export function getConflictingAttendeesPainText(
  schedulingSlot: {
    attendanceImpacts: {
      calendarId: string;
      hasConflict: boolean;
      conflictingMeetings: string[];
    }[];
  },
  persons: Person[],
): [string | null, number] {
  const { attendanceImpacts } = schedulingSlot;

  const conflictingAttendees: string[] = [];
  const conflictingMeetings = new Set();
  attendanceImpacts?.forEach((impact) => {
    if (impact.hasConflict) {
      conflictingAttendees.push(impact.calendarId);

      if (hasOnlyConflictingMeetingWithUnknownMeeting(impact)) {
        // We still want to communicate conflicts with private meetings in the pain-slot messaging
        // Thus handle it here which will account for it in to the message construction
        conflictingMeetings.add(GENERIC_MEETING_TITLE);
      }

      impact?.conflictingMeetings?.forEach((meeting) => conflictingMeetings.add(meeting));
    }
  });

  if (conflictingAttendees.length === 0) {
    return [null, 0];
  }

  const { attendeesText, isPlural, numImpacted } = getAttendeesText(
    persons,
    conflictingAttendees,
    true,
  );
  const conflictingMeetingsArr = [...conflictingMeetings];

  // This case most likely won't be true.
  if (conflictingMeetingsArr.length === 0) {
    if (isPlural) {
      return [`${attendeesText} have a conflict`, numImpacted];
    }
    return [`${attendeesText} has a conflict`, numImpacted];
  }

  if (conflictingMeetingsArr.length === 1) {
    if (isPlural) {
      return [`${attendeesText} have a conflict with ${conflictingMeetingsArr[0]}`, numImpacted];
    }
    return [`${attendeesText} has a conflict with ${conflictingMeetingsArr[0]}`, numImpacted];
  }

  // Multiple people, multiple conflicts.
  if (isPlural) {
    return [
      `${attendeesText} have a conflict with ${conflictingMeetingsArr.length} meetings`,
      numImpacted,
    ];
  }
  // One person multiple conflicts. Hope we never recommend this case!
  return [
    `${attendeesText} has a conflict with ${conflictingMeetingsArr.length} meetings`,
    numImpacted,
  ];
}

export function getInconveniencyText(inconveniences: Inconveniences[]) {
  if (inconveniences.length > 1) {
    return "Inconvenient";
  } else if (inconveniences.length === 1) {
    switch (inconveniences[0]) {
      case Inconveniences.MissedLunch:
        return "Over lunch";
      case Inconveniences.OutsideMeetingHours:
        return "Outside meeting hours";
      case Inconveniences.OutsideWorkingHours:
        return "Outside working hours";
      case Inconveniences.OnNoMeetingDay:
        return "On a no-meeting day";
      case Inconveniences.NoMeetingRelief:
        return "Many consecutive meetings";
      case Inconveniences.External:
        return "Outside your domain, this time may not work";
      case Inconveniences.Uncrawled:
        return "Can't access calendar, time may not work";
      case Inconveniences.Other:
      default:
    }
  }

  return "Inconvenient";
}

function getUnavailableAttendeesPainText(
  schedulingSlot: {
    summary: {
      unavailableAttendees: string[];
    };
    attendanceImpacts: {
      calendarId: string;
      unavailabilities: Unavailabilities[];
    }[];
  },
  persons: Person[],
): [string | null, number] {
  const { summary, attendanceImpacts } = schedulingSlot;
  const unavailabilityMap = generateUnavailabilityMap(attendanceImpacts);

  // TODO: not proud of all this. refactor
  let count = 0;
  let maybeOnlyKey = "";
  Object.keys(unavailabilityMap)?.map((key) => {
    if (unavailabilityMap[key].length > 0) {
      count++;
      maybeOnlyKey = key;
    }
  });

  const { attendeesText, isPlural, numImpacted } = getAttendeesText(
    persons,
    summary.unavailableAttendees,
    true,
  );

  if (count > 1) {
    return [`${attendeesText} ${isPlural ? "are" : "is"} unavailable`, numImpacted];
  } else if (count === 1) {
    const displayKey = "unavailable";
    switch (maybeOnlyKey) {
      case Unavailabilities.FarOutsideWorkingHours:
        break;
      default:
        break;
    }
    return [`${attendeesText} ${isPlural ? "are" : "is"} ${displayKey}`, numImpacted];
  }

  return [null, 0];
}

export type GetIndividualAttendeeInconvenienceTextAttendanceImpact = Pick<
  ISchedulingAttendanceImpact,
  "inconveniences"
>;
export function getIndividualAttendeeInconvenienceText(
  attendanceImpact: GetIndividualAttendeeInconvenienceTextAttendanceImpact,
) {
  let pains: { pain: Pain; value: string }[] = [];

  pains = attendanceImpact.inconveniences.map((pain) => {
    switch (pain) {
      case Inconveniences.MissedLunch:
        return { pain, value: "Over lunch" };
      case Inconveniences.OutsideMeetingHours:
        return { pain, value: "Outside meeting hours" };
      case Inconveniences.OutsideWorkingHours:
        return { pain, value: "Outside working hours" };
      case Inconveniences.OnNoMeetingDay:
        return { pain, value: "On a no-meeting day" };
      case Inconveniences.NoMeetingRelief:
        return { pain, value: "Many consecutive meetings" };
      case Inconveniences.External:
        return { pain, value: "Outside your domain, this time may not work" };
      case Inconveniences.Uncrawled:
        return { pain, value: "Can't access calendar, time may not work" };
      case Inconveniences.Other:
      default:
        return { pain: pain, value: "Inconvenient" };
    }
  });

  return pains;
}

// Returns a list of ["Pains and Incons" for "getAttendeesText'""];
// Example: ["Over Lunch for Susan", ...]
export function getAttendeesPainText(
  schedulingSlot: {
    summary: {
      unavailableAttendees: string[];
    };
    attendanceImpacts: {
      calendarId: string;
      hasConflict: boolean;
      conflictingMeetings: string[];
      inconveniences: Inconveniences[];
      unavailabilities: Unavailabilities[];
    }[];
  },
  persons: Person[],
  ignoreUnavailableAttendees?: boolean,
) {
  const pains: { pain: Pain; value: string; numImpacted: number }[] = [];

  // CONFLICTS
  const [conflicts, numConflicted] = getConflictingAttendeesPainText(schedulingSlot, persons);
  if (conflicts) {
    pains.push({ pain: "Conflict", value: conflicts, numImpacted: numConflicted });
  }

  // INCONVENIENCES
  const { summary, attendanceImpacts } = schedulingSlot;
  const inconvenienceMap = generateInconvenienceMap(attendanceImpacts);

  Object.keys(inconvenienceMap).map((pain) => {
    const inconveniencedCalendarIds = inconvenienceMap[pain];
    const filteredInconveniencedCalendarIds =
      ignoreUnavailableAttendees && summary.unavailableAttendees
        ? inconveniencedCalendarIds.filter((c) => summary.unavailableAttendees.indexOf(c) < 0)
        : inconveniencedCalendarIds;

    if (filteredInconveniencedCalendarIds.length > 0) {
      const inconveniencedPersons = persons.filter(
        (p) => filteredInconveniencedCalendarIds.indexOf(p.primaryEmail) > -1,
      );
      const { attendeesText, numImpacted } = getAttendeesText(
        inconveniencedPersons,
        filteredInconveniencedCalendarIds,
      );
      pains.push({
        pain: pain as Pain,
        value: getInconvenienceStringForAttendees(pain as Pain, attendeesText),
        numImpacted,
      });
    }
  });

  // UNAVAILABLE
  const [unavailables, numImpacted] = ignoreUnavailableAttendees
    ? getUnavailableAttendeesPainText(schedulingSlot, persons)
    : [null, 0];
  if (unavailables) {
    pains.push({
      pain: Unavailabilities.FarOutsideWorkingHours,
      value: unavailables,
      numImpacted,
    });
  }

  return pains;
}
